import React, { useCallback, useContext, useEffect, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { getDateWithLongMonth } from "src/utils/Date";
import { Route, selectRoute } from "src/config/routes";
import { UI } from "@wwimmo/ui";
import styles from "./Protocol.module.css";
import { useParams, useHistory } from "react-router";
import { runInAction } from "mobx";
import { ProtocolPreview } from "./preview/ProtocolPreview";
import { ColorStyle } from "src/utils/Colors";
import { InlineCommunicationTimelineEntry } from "src/screens/assemblies/assembly/communication/InlineCommunicationTimelineEntry";
import { COMMUNICATION_TIMELINE_ENTRY } from "src/stores/CommunicationStore";
import { FileSelectorItem } from "src/components/fileSelector/FileSelectorItem";
import { FILE_TYPE, FILE_STATE } from "src/enums";
import { AssemblyUnlockModal } from "src/screens/assemblies/assembly/agenda/modal/AssemblyUnlockModal";

interface Params {
    assemblyid?: string;
}

type ProtocolButtonsProps = {
    assemblyId: string;
};

type SignaturesField = "title" | "text";

const ProtocolInputs = observer(function ProtocolInputs() {
    const { t } = useTranslation();
    const { protocolStore, navStore, assemblyStore } = useContext(RootStoreContext);
    const isReadOnly = assemblyStore.isAssemblyReadOnly;

    const uploadedSignatureIds = useMemo(
        () => new Set(protocolStore.signatureFiles.map((f) => f.signatureid)),
        [protocolStore.signatureFiles]
    );

    const onSignatureImageFileSelected = useCallback(
        (signatureId: string) => async (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;

            if (uploadedSignatureIds.has(signatureId)) {
                await protocolStore.deleteSignatureFiles(signatureId);
            }

            protocolStore.setSelectedSignatureImageFile(file);
            await protocolStore.uploadAssemblySignatureImage(signatureId);
        },
        [protocolStore, uploadedSignatureIds]
    );

    const handleSignaturesFieldChange = useCallback(
        (id: string, field: SignaturesField) => (e: React.ChangeEvent<HTMLInputElement>) => {
            e.preventDefault();
            runInAction(() => {
                const sig = protocolStore.signaturesList.find((s) => s.id === id);
                if (sig) sig[field] = e.target.value;
            });
        },
        [protocolStore]
    );

    const onDeleteSignature = useCallback(
        (signatureId: string) => () => {
            protocolStore.deleteSignature(signatureId);
        },
        [protocolStore]
    );

    const onDeleteSignatureFile = useCallback(
        (signatureId: string) => async () => {
            await protocolStore.deleteSignatureFiles(signatureId);
        },
        [protocolStore]
    );

    return (
        <>
            {protocolStore.signaturesList.map((signature) => {
                const hasFile = uploadedSignatureIds.has(signature.id);
                const sigFile = protocolStore.signatureFiles.find((f) => f.signatureid === signature.id);
                const iFile = sigFile
                    ? {
                          fileId: sigFile.fileid,
                          name: sigFile.name ?? undefined,
                          mimetype: sigFile.mimetype ?? undefined,
                          type: FILE_TYPE.ASSEMBLY_SIGNATURE,
                          state: FILE_STATE.LOADED_FROM_DB
                      }
                    : undefined;
                const signatureIcon = navStore.hasFeatureFlagAssemblyFaksimileSignature ? (
                    <div className={styles.SignatureIconAndImage}>
                        <label
                            htmlFor={`sig-upload-${signature.id}`}
                            className={styles.SignatureIcon}
                            style={isReadOnly ? { pointerEvents: "none", opacity: 0.6 } : undefined}
                        >
                            <UI.Icon
                                icon={hasFile ? UI.SVGIcon.Check : UI.SVGIcon.ContractEdit}
                                color={hasFile ? ColorStyle("primary") : "black"}
                                size={25}
                            />
                            <input
                                id={`sig-upload-${signature.id}`}
                                type="file"
                                accept="image/*,.pdf,application/pdf,.p7b,.p7c,.p7s,.p7m,application/pkcs7-mime,application/pkcs7-signature,application/x-pkcs7-certificates"
                                hidden
                                disabled={isReadOnly}
                                onChange={onSignatureImageFileSelected(signature.id)}
                            />
                        </label>
                        {iFile && (
                            <FileSelectorItem
                                key={`sig-file-${signature.id}`}
                                label={t("screens.assemblies.protocolcontent.signature_not_uploaded")}
                                fileType={FILE_TYPE.ASSEMBLY_SIGNATURE}
                                file={iFile}
                                onDeleteFilePropFunc={onDeleteSignatureFile(signature.id)}
                                canDelete={!isReadOnly}
                                fileSelectorItemStyles={{ width: "200px", height: "120px" }}
                            />
                        )}
                    </div>
                ) : undefined;

                return (
                    <div className={styles.InputRow} key={signature.id}>
                        <UI.Input
                            className={styles.InputSignatures}
                            label={t("labels.name").toString()}
                            value={signature.text || ""}
                            onChange={handleSignaturesFieldChange(signature.id, "text")}
                            placeholder={t("screens.assemblies.protocolcontent.name_placeholder").toString()}
                            disabled={isReadOnly}
                        />
                        <UI.Input
                            className={styles.InputSignatures}
                            label={`${t("screens.assemblies.protocolcontent.function")} *`}
                            value={signature.title || ""}
                            onChange={handleSignaturesFieldChange(signature.id, "title")}
                            placeholder={t("screens.assemblies.protocolcontent.function_placeholder").toString()}
                            disabled={isReadOnly}
                        />
                        {isReadOnly ? undefined : (
                            <UI.Icon
                                className={styles.TrashIcon}
                                icon={UI.SVGIcon.Trash}
                                onClick={onDeleteSignature(signature.id)}
                            />
                        )}
                        {signatureIcon && !hasFile ? (
                            <UI.OverlayTrigger
                                placement="top"
                                overlay={
                                    <UI.Tooltip id={`sig-tooltip-${signature.id}`}>
                                        {t("screens.assemblies.protocolcontent.signature_not_uploaded")}
                                    </UI.Tooltip>
                                }
                            >
                                {signatureIcon}
                            </UI.OverlayTrigger>
                        ) : (
                            (signatureIcon ?? null)
                        )}
                    </div>
                );
            })}
        </>
    );
});

const ProtocolAddSignatureButton = observer(function ProtocolAddSignatureButton() {
    const { t } = useTranslation();
    const { protocolStore, assemblyStore } = useContext(RootStoreContext);

    const onClickAddSignature = useCallback(
        (e: any) => {
            e.preventDefault();

            protocolStore.addSignature();
        },
        [protocolStore]
    );

    return (
        <>
            <UI.Button
                label={"+ " + t("screens.assemblies.protocolcontent.add_name").toString()}
                className={styles.ButtonAddSignature}
                onClick={onClickAddSignature}
                disabled={protocolStore.signaturesList.length >= 4 || assemblyStore.isAssemblyReadOnly}
            />
        </>
    );
});

const ProtocolSignatureButtons = observer(function ProtocolSignatureButtons({ assemblyId }: ProtocolButtonsProps) {
    const { t } = useTranslation();
    const { protocolStore, assemblyStore } = useContext(RootStoreContext);

    const hasChanges = protocolStore.hasSignaturesChanged || protocolStore.hasProtocolDatePlaceChanged;
    const isReadOnly = assemblyStore.isAssemblyReadOnly;

    const onSaveAssemblyProtocol = useCallback(
        (e: any) => {
            e.preventDefault();
            if (protocolStore.hasSignaturesChanged) {
                protocolStore.saveSignatures(assemblyId);
            }
            if (protocolStore.hasProtocolDatePlaceChanged) {
                protocolStore.saveProtocolDatePlace(assemblyId);
            }
        },
        [protocolStore, assemblyId]
    );

    const onDiscardAssemblyProtocol = useCallback(
        (e: any) => {
            e.preventDefault();
            protocolStore.discardAssemblyProtocol();
        },
        [protocolStore]
    );

    return (
        <div className={styles.ButtonRow}>
            <UI.Button
                className={styles.ButtonSave}
                label={t("labels.save").toString()}
                onClick={onSaveAssemblyProtocol}
                disabled={!hasChanges || isReadOnly}
            />
            <UI.Button
                className={styles.ButtonDiscard}
                label={t("labels.discard").toString()}
                onClick={onDiscardAssemblyProtocol}
                disabled={!hasChanges || isReadOnly}
                variant="outline-primary"
            />
        </div>
    );
});

const ProtocolDatePlaceInputs = observer(function ProtocolDatePlaceInputs() {
    const { t } = useTranslation();
    const { protocolStore, assemblyStore } = useContext(RootStoreContext);
    const isReadOnly = assemblyStore.isAssemblyReadOnly;

    const onProtocolDateChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            protocolStore.setProtocolDate(e.target.value);
        },
        [protocolStore]
    );

    const onProtocolPlaceChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            protocolStore.setProtocolPlace(e.target.value);
        },
        [protocolStore]
    );

    return (
        <div className={styles.InputDatePlaceRow}>
            <UI.Input
                className={styles.InputPlace}
                label={t("screens.realestate.assembly.city").toString()}
                value={protocolStore.protocolPlace}
                onChange={onProtocolPlaceChange}
                disabled={isReadOnly}
            />
            <UI.Input
                className={styles.InputDate}
                type="date"
                label={t("screens.realestate.assembly.date").toString()}
                value={protocolStore.protocolDate}
                onChange={onProtocolDateChange}
                disabled={isReadOnly}
            />
        </div>
    );
});

const ProtocolBase = () => {
    const { t } = useTranslation();
    const { assemblyid } = useParams<Params>();
    const { navStore, assemblyStore, authStore, protocolStore, agendaitemStore } = useContext(RootStoreContext);
    const history = useHistory();
    const currentAssemblyId = assemblyid || assemblyStore.currentAssembly?.id || "";

    const onClickUnlockAssembly = useCallback(() => {
        agendaitemStore.setIsModalUnlockDisplayed(true);
    }, [agendaitemStore]);

    useEffect(() => {
        if (assemblyid) {
            if (!assemblyStore.currentAssembly) {
                assemblyStore.loadCurrentAssembly(assemblyid).then(() => {
                    protocolStore.setInitialProtocol();
                });
            } else {
                protocolStore.setInitialProtocol();
            }
        }
        if (assemblyStore.currentAssembly) {
            navStore.setTitle(
                `${assemblyStore.currentAssembly.title} am ${getDateWithLongMonth(
                    assemblyStore.currentAssembly.date || ""
                )}`
            );
        }

        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);
        navStore.setBackbutton(
            selectRoute(Route.assemblies, authStore.user?.role),
            t("screens.assemblies.sidebar_title").toString()
        );
    }, [assemblyStore, authStore, navStore, t, assemblyStore.currentAssembly, assemblyid, protocolStore]);

    useEffect(() => {
        protocolStore.loadSignaturesForProtocol(assemblyid || "");
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (assemblyStore.currentAssembly?.livemode) {
            history.push(
                selectRoute(Route.assemblyLiveMode, authStore.user?.role, {
                    assemblyid: assemblyStore.currentAssembly?.id || ""
                })
            );
        }
    }, [assemblyStore.currentAssembly?.livemode, history, authStore.user?.role, assemblyStore.currentAssembly?.id]);

    return (
        <UI.Container className="pt-4">
            {assemblyStore.isAssemblyReadOnly ? (
                <>
                    <div className={styles.BlockedBanner}>
                        <div className={styles.BannerLeft}>
                            <UI.Icon className={styles.LockIcon} icon={UI.SVGIcon.Lock} size={"small"} />
                            <div className={styles.BannerText}>{t("screens.assemblies.agenda.locked_info")}</div>
                        </div>
                        <div className={styles.BannerLink} onClick={onClickUnlockAssembly}>
                            {t("screens.assemblies.agenda.unlock").toUpperCase()}
                        </div>
                    </div>
                    <AssemblyUnlockModal />
                </>
            ) : null}
            <UI.Card title={t("screens.assemblies.protocolcontent.signatures").toString()}>
                <ProtocolInputs />
                <ProtocolAddSignatureButton />
                <ProtocolDatePlaceInputs />
                <ProtocolSignatureButtons assemblyId={currentAssemblyId} />
            </UI.Card>
            <ProtocolPreview assemblyId={currentAssemblyId} />
            {protocolStore.protocolFileId && !protocolStore.isProtocolPdfLoading ? (
                <InlineCommunicationTimelineEntry
                    assemblyId={currentAssemblyId}
                    entryState={COMMUNICATION_TIMELINE_ENTRY.PROTOCOL_PUBLISHED}
                />
            ) : undefined}
        </UI.Container>
    );
};

export const Protocol = Sentry.withProfiler(observer(ProtocolBase));
